
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import "./line-chart.css";
import TimeRangeDropdown from "../../time-range-filter/time-range-dropdown";
import UtilHelper from "../../../utils/UtilHelper";
import SimpleSpinner from "../../../components/loader";
import EmptyState from "../../emptystate";
import { GetImages } from "../../../utils/GetImages";
import { useCurrencySymbol } from "../../../hooks/useCurrencySymbol";

interface CashFlowItem {
  /** Expected format: "MM/YYYY"  (e.g. "01/2025") */
  month: string;
  revenue: number;
  expense: number;
}

interface CashFlowOverviewProps {
  data: CashFlowItem[];
  isLoading: boolean;
  timeRanges: string[];
  selectedTimeRange: string;
  onTimeRangeChange: (range: string) => void;
  className?: string;
  /** New color props */
  revenueColor?: string;
  expenseColor?: string;
}

/* ------------------------------------------------------------------ */
/*  X-axis label formatter – "Jan 25", "Feb 25", …                     */
/* ------------------------------------------------------------------ */
const formatMonthLabel = (monthInput: any): string => {
  let month: number, year: number;

  if (typeof monthInput === "number" && monthInput >= 1 && monthInput <= 12) {
    month = monthInput;
    year = new Date().getFullYear();
  }
  else if (typeof monthInput === "string" && monthInput.includes("/")) {
    const [m, y] = monthInput.split("/");
    month = parseInt(m, 10);
    year = parseInt(y, 10);
  }
  else if (typeof monthInput === "string" && monthInput.includes("-")) {
    const [y, m] = monthInput.split("-");
    month = parseInt(m, 10);
    year = parseInt(y, 10);
  }
  else {
    month = typeof monthInput === "number" ? monthInput : parseInt(monthInput, 10);
    year = new Date().getFullYear();
  }

  if (isNaN(month) || month < 1 || month > 12) month = 1;
  if (isNaN(year)) year = new Date().getFullYear();

  const date = new Date(year, month - 1, 1);
  const shortYear = date.getFullYear().toString().slice(-2);

  return date
    .toLocaleString("default", { month: "short" })
    .concat(` ${shortYear}`);
};



/* ------------------------------------------------------------------ */
/*  Custom Tooltip                                                    */
/* ------------------------------------------------------------------ */
/* ------------------------------------------------------------------ */
/*  Custom Tooltip                                                    */
/* ------------------------------------------------------------------ */
const CustomTooltip = ({
  active,
  payload,
  currencySymbol = "$"
}: {
  active?: boolean;
  payload?: any[];
  currencySymbol?: string;
}) => {
  if (active && payload && payload.length >= 2) {
    const rev = payload.find((p) => p.dataKey === "revenue");
    const exp = payload.find((p) => p.dataKey === "expense");

    return (
      <div className="toolTipContainer2">
        <p className="sub_text fw-bold">{payload[0].payload.monthLabel}</p>

        <div className="d-flex gap-4 align-items-center vacancyDataContainer">
          <div>
            <p className="sub_text grey_subtext1 mb-1">Revenue</p>
            <p className="normal_text black">{UtilHelper.formatCurrencyFullUnder10K(rev.value, true, currencySymbol)}</p>
          </div>
          <div>
            <p className="sub_text grey_subtext1 mb-1">Expense</p>
            <p className="normal_text black">{UtilHelper.formatCurrencyFullUnder10K(exp.value, true, currencySymbol)}</p>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

export default function CashFlowOverview({
  data,
  isLoading,
  timeRanges,
  selectedTimeRange,
  onTimeRangeChange,
  className = "",
  revenueColor = "#0066ff",   // default blue
  expenseColor = "#ff6b35",   // default orange
}: CashFlowOverviewProps) {
  const currencySymbol = useCurrencySymbol();
  const chartData = data.map((d) => ({
    ...d,
    month: d.month,
    monthLabel: formatMonthLabel(d.month),
  }));

  return (
    <div className={`cash-flow-container ${className}`}>
      {/* ---------- HEADER ---------- */}
      <div className="cash-flow-header">
        <h2 className="cash-flow-title">Cash Flow Overview</h2>

        <TimeRangeDropdown
          timeRanges={timeRanges}
          defaultTimeRange={selectedTimeRange}
          onTimeRangeChange={onTimeRangeChange}
        />
      </div>

      {/* ---------- CHART ---------- */}
      <div className="chart-wrapper">
        {isLoading ? (
          <div className="loading-spinner">
            <SimpleSpinner myClassName={"Icon50 mt-5"} />
          </div>
        ) : chartData.length === 0 ? (
          <EmptyState
            icon={GetImages.emptyStateData}
            className="w-100"
            isBorder={false}
            heading="No Data found"
            isButton={false}
          />
        ) : (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 30, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="var(--Grey-Divider)" />
              <XAxis
                dataKey="monthLabel"
                stroke="var(--Grey-Subtext2)"
                style={{ fontSize: "14px" }}
              />
              <YAxis
                stroke="var(--Grey-Subtext2)"
                style={{ fontSize: "14px" }}
                tickFormatter={(tick) => UtilHelper.formatCurrencyFullUnder10K(tick, true, currencySymbol)}
                domain={[0, "dataMax + 10000"]}
                // bug/amount-format-fix: below $10,000 these ticks now render the full amount
                // (e.g. "$9,999.00", ~9 chars) instead of the old abbreviated "$9.0K" (~5 chars) --
                // wider than recharts' default 60px axis width at this 14px font, risking left-edge
                // clipping. 85px comfortably fits the widest sub-$10K label (anything >= $10,000
                // abbreviates instead, so the longest full label stays under "$9,999.99").
                width={85}
              />
              <Tooltip content={<CustomTooltip currencySymbol={currencySymbol} />} />
              <Legend
                wrapperStyle={{ paddingTop: "20px", fontSize: "14px" }}
                iconType="circle"
                formatter={(value) => <span style={{ color: "var(--Grey-Heading)" }}>{value}</span>}
              />
              <Line
                type="monotone"
                dataKey="revenue"
                stroke={revenueColor}
                strokeWidth={2}
                dot={{ fill: revenueColor, r: 4 }}
                activeDot={{ r: 6 }}
                name="Revenue"
              />
              <Line
                type="monotone"
                dataKey="expense"
                stroke={expenseColor}
                strokeWidth={2}
                dot={{ fill: expenseColor, r: 4 }}
                activeDot={{ r: 6 }}
                name="Expense"
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}